/**
 * Avisos compartidos de los flujos de IA en streaming (OT-0040): la espera
 * del modelo y el error del flujo. Los usan los modales de sección,
 * "generar todo", proponer estructura e IA de comentarios.
 */

import { Clock, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { WaitingPayload } from '../../api/agents'
import { formatDuration } from '../../lib/formatDuration'

/** A partir de aquí la espera se señala como anormal. */
const SLOW_MODEL_SECONDS = 60

/** Tiempo que lleva el modelo sin responder y, pasado el umbral, el aviso
 * de que tarda más de lo normal. Sin espera reportada no muestra nada. */
export function AiWaitingNotice({ waiting }: { waiting: WaitingPayload | null }) {
  const { t } = useTranslation('ai')
  if (waiting === null) return null
  const slow = waiting.elapsed_s >= SLOW_MODEL_SECONDS
  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${
        slow
          ? 'border-review/40 bg-review-soft/30 text-ink'
          : 'border-line bg-surface-muted/40 text-ink-muted'
      }`}
    >
      <Clock
        className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${slow ? 'text-review' : 'text-ink-subtle'}`}
        aria-hidden="true"
      />
      <div>
        <p>
          {t('waiting.elapsed', {
            elapsed: formatDuration(waiting.elapsed_s * 1000, t),
          })}
        </p>
        {slow && <p className="mt-0.5 font-medium">{t('waiting.slow')}</p>}
      </div>
    </div>
  )
}

/** Error de un flujo de IA: copy del catálogo `ai:errors` si existe para el
 * código; si no, el mensaje que ya llega traducido (backend o
 * `STREAM_INTERRUPTED`). */
export function AiErrorNotice({
  code,
  message,
}: {
  code?: string | undefined
  message: string
}) {
  const { t } = useTranslation('ai')
  const localized = code ? t(`errors.${code}`, { defaultValue: '' }) : ''
  const display = localized || t('errors.generic', { message })
  return (
    <div className="flex items-start gap-3 rounded-md border border-critical/40 bg-critical-soft/30 p-3">
      <XCircle
        className="mt-0.5 h-4 w-4 shrink-0 text-critical"
        aria-hidden="true"
      />
      <div className="text-sm text-ink">
        {code && (
          <p className="font-mono text-[10px] uppercase tracking-wider text-ink-subtle">
            {code}
          </p>
        )}
        <p>{display}</p>
      </div>
    </div>
  )
}
